/**
 * @file NoticeStrip.tsx
 * @description Полоса-предупреждение документа (phase19.24 этап 1, шаг 1).
 *
 * Место в каркасе — сразу под плитками шапки: «Комментарий: …», «Черновик
 * остаток не двигает…», «N позиций без резерва…», «Задание взял другой
 * сотрудник». До контракта каждая такая строка верстались заново — `<p>` с
 * `bg-surface-low`, `Alert severity="info"`, ручной div с иконкой, — и
 * отступы между документами не совпадали.
 *
 * Тона — закрытый реестр (`info` / `warning` / `error`): новый вид полосы =
 * запись в `TONE_META`, а не ветка в разметке. Иконка и цвет приходят из
 * реестра, отступы одинаковые у всех.
 *
 * `Alert` остаётся для форм и диалогов (ошибка запроса рядом с полем); полоса
 * документа — это `NoticeStrip`.
 *
 * ```tsx
 * <NoticeStrip tone="warning">1 позиция без резерва: товар спишется при проведении.</NoticeStrip>
 * <NoticeStrip tone="info" action={<Button>Пересобрать</Button>}>Строк нет</NoticeStrip>
 * ```
 */

import { AlertCircle, Info, TriangleAlert, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Тон полосы: пояснение, предупреждение, ошибка/запрет. */
export type NoticeTone = "info" | "warning" | "error";

interface ToneMeta {
  icon: LucideIcon;
  /** Фон полосы — без бордера (No-Line Rule). */
  strip: string;
  /** Цвет иконки. */
  iconClass: string;
}

/** Реестр тонов: новый тон = запись здесь, не условие в компоненте. */
const TONE_META: Record<NoticeTone, ToneMeta> = {
  info: { icon: Info, strip: "bg-info/8", iconClass: "text-info" },
  warning: { icon: TriangleAlert, strip: "bg-warning/12", iconClass: "text-warning" },
  error: { icon: AlertCircle, strip: "bg-destructive/10", iconClass: "text-destructive" },
};

interface Props {
  tone?: NoticeTone;
  /** Текст полосы — одна фраза без «внимание!» и восклицательных знаков. */
  children: ReactNode;
  /** Кнопка справа: «Пересобрать задание», «Повторить». */
  action?: ReactNode;
  /** Подпись-заголовок над текстом; обычно не нужна. */
  title?: ReactNode;
  className?: string;
}

export function NoticeStrip({ tone = "info", children, action, title, className }: Props) {
  const meta = TONE_META[tone];
  const Icon = meta.icon;
  return (
    <div
      data-slot="notice-strip"
      data-tone={tone}
      // Ошибку читает скринридер сразу (alert), пояснение — в порядке чтения.
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-3 rounded-xl px-4 py-3 text-sm text-on-surface",
        meta.strip,
        className,
      )}
    >
      <Icon className={cn("mt-0.5 size-4 shrink-0", meta.iconClass)} aria-hidden />
      <div className="min-w-0 flex-1">
        {title ? <div className="font-medium">{title}</div> : null}
        {children}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
